import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { RootState } from "../store"
import BaseApi from "../../api/api"
import { NotificationService } from "../../features/NotificationService/NotificationService"
import { SHOP_IDENT } from "@acc/shared"

export { SHOP_IDENT }

export type PriceType = {
  margin?: number
  profit?: number
  logistic?: number
  ff?: number
  coef?: number
  tax?: number
  sale?: number
  priemka?: number
  ekv?: number
  mila?: number
  liter_price?: number
  extra_liter_price?: number
  promo_profit?: number
  promo_margin?: number
  stale_profit?: number
  stale_margin?: number
}

type ShopSettingsType = {
  price: PriceType
  amountParams?: { count: number; amount: number }[]
}

type ActiveShopsType<
  T =
    | SHOP_IDENT.ICON
    | SHOP_IDENT.KATREN
    | SHOP_IDENT.KATREN_OZON
    | SHOP_IDENT.KATREN_YANDEX
    | SHOP_IDENT.PODRUZHKA
    | SHOP_IDENT.METRO
    | SHOP_IDENT.SAMSON
    | SHOP_IDENT.SAMSON_YANDEX,
> = T[]

type initialStateType = {
  [key in SHOP_IDENT]?: ShopSettingsType
} & {
  activeShops: ActiveShopsType
  feedShops: string[]
  shopName: string
}

const initialState: initialStateType = {
  activeShops: [],
  feedShops: [],
  shopName: "",
}

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    setPriceParams: (
      state,
      action: PayloadAction<{
        shop: SHOP_IDENT
        price: PriceType
        amountParams?: { count: number; amount: number }[]
      }>,
    ) => {
      const newState = {
        ...state[action.payload.shop],
        price: action.payload.price,
      }
      action.payload.amountParams &&
        (newState.amountParams = action.payload.amountParams)
      state[action.payload.shop] = newState
    },
    setSettingsState: (state, action: PayloadAction<initialStateType>) => {
      Object.keys(action.payload).forEach((key) => {
        const k = key as keyof initialStateType
        ;(state as Record<string, unknown>)[k] = action.payload[k]
      })
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateSettingsState.fulfilled, (state, action) => {
        Object.keys(action.payload).forEach((key) => {
          const k = key as keyof initialStateType
          ;(state as Record<string, unknown>)[k] = action.payload[k]
        })
      })
      .addCase(updateSettingsState.rejected, (state, action) => {
        console.error(action.error)
      })
      .addCase(updatePricesParams.fulfilled, (state, action) => {
        NotificationService.showNotification({
          message: action.payload.message,
          level: (action.payload.success && "success") || "error",
        })
      })
  },
})

export const updateSettingsState = createAsyncThunk(
  "settings/load",
  async () => {
    const response = await BaseApi.settings()
    return response.data
  },
)

export const updatePricesParams = createAsyncThunk(
  "settings/priceparam/update",
  async (
    params: {
      shop: string
      price: PriceType
      amountParams?: { count: number; amount: number }[]
    },
    { dispatch },
  ) => {
    return BaseApi.updatePriceParams(
      params.shop,
      params.price,
      params.amountParams,
    ).then((resp) => resp.data)
  },
)

export const updateShopName = createAsyncThunk(
  "settings/shopName/update",
  async (shopName: string) => {
    return BaseApi.updateShopName(shopName).then((resp) => resp.data)
  },
)

export const selectSettings = (state: RootState) => state.settings
export const selectActiveShops = (state: RootState) =>
  state.settings.activeShops
export const selectFeedShopIdents = (state: RootState) =>
  state.settings.feedShops
export const selectShopName = (state: RootState) => state.settings.shopName

export default settingsSlice.reducer

export const { setSettingsState, setPriceParams } = settingsSlice.actions
